import { useMemo, useState } from 'react';
import { MapPinned, Route } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import type { EdgeRecord, LocationGeoMap, RouteCandidate } from '@/features/network/models';
import { effectiveLatency } from '@/features/routing/solver';
import { formatCapacity, formatLatency } from '@/lib/format';

type MapViewProps = {
  edges: EdgeRecord[];
  primary: RouteCandidate | null;
  backup: RouteCandidate | null;
  locationGeoMap?: LocationGeoMap;
};

type ProjectedNode = {
  id: string;
  lat: number;
  lon: number;
  x: number;
  y: number;
  inRoute: boolean;
};

type ProjectedEdge = {
  edge: EdgeRecord;
  from: ProjectedNode;
  to: ProjectedNode;
  isPrimary: boolean;
  isBackup: boolean;
};

function edgeHighlightKeys(route: RouteCandidate | null) {
  return new Set(route?.edgeKeys ?? []);
}

function makeEdgeKey(edge: EdgeRecord) {
  return `${edge.id}::${[edge.from, edge.to].sort().join(' ⇄ ')}`;
}

export function MapView({ edges, primary, backup, locationGeoMap = {} }: MapViewProps) {
  const [selection, setSelection] = useState<{ title: string; body: string } | null>(null);

  const { nodes, projectedEdges, skippedNodeCount, skippedEdgeCount, width, height } = useMemo(() => {
    const routeNodeSet = new Set([...(primary?.nodes ?? []), ...(backup?.nodes ?? [])]);
    const points = new Map<string, { lat: number; lon: number }>();
    const encounteredNodes = new Set<string>();

    for (const edge of edges) {
      encounteredNodes.add(edge.from);
      encounteredNodes.add(edge.to);
    }

    for (const nodeId of encounteredNodes) {
      const point = locationGeoMap[nodeId]?.point;
      if (!point) continue;
      if (!Number.isFinite(point.lat) || !Number.isFinite(point.lon)) continue;
      points.set(nodeId, { lat: point.lat, lon: point.lon });
    }

    const latValues = Array.from(points.values()).map((value) => value.lat);
    const lonValues = Array.from(points.values()).map((value) => value.lon);

    const minLat = latValues.length ? Math.min(...latValues) : 0;
    const maxLat = latValues.length ? Math.max(...latValues) : 1;
    const minLon = lonValues.length ? Math.min(...lonValues) : 0;
    const maxLon = lonValues.length ? Math.max(...lonValues) : 1;

    const mapWidth = 1200;
    const mapHeight = 700;
    const padding = 36;
    const lonSpan = Math.max(maxLon - minLon, 0.000001);
    const latSpan = Math.max(maxLat - minLat, 0.000001);

    const projectedNodes: ProjectedNode[] = Array.from(points.entries()).map(([id, point]) => ({
      id,
      lat: point.lat,
      lon: point.lon,
      x: padding + ((point.lon - minLon) / lonSpan) * (mapWidth - padding * 2),
      y: padding + (1 - (point.lat - minLat) / latSpan) * (mapHeight - padding * 2),
      inRoute: routeNodeSet.has(id),
    }));

    const nodeById = new Map(projectedNodes.map((node) => [node.id, node]));
    const primarySet = edgeHighlightKeys(primary);
    const backupSet = edgeHighlightKeys(backup);

    const projected: ProjectedEdge[] = [];
    let skippedEdges = 0;

    for (const edge of edges) {
      const from = nodeById.get(edge.from);
      const to = nodeById.get(edge.to);
      if (!from || !to) {
        skippedEdges += 1;
        continue;
      }

      const edgeKey = makeEdgeKey(edge);
      projected.push({
        edge,
        from,
        to,
        isPrimary: primarySet.has(edgeKey),
        isBackup: backupSet.has(edgeKey),
      });
    }

    return {
      nodes: projectedNodes,
      projectedEdges: projected,
      skippedNodeCount: encounteredNodes.size - projectedNodes.length,
      skippedEdgeCount: skippedEdges,
      width: mapWidth,
      height: mapHeight,
    };
  }, [edges, locationGeoMap, primary, backup]);

  return (
    <Card className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-lg font-semibold">
            <MapPinned className="h-5 w-5" />
            Гео-карта сети (MVP)
          </div>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            Узлы и рёбра проецируются по lat/lon в SVG-плоскость. Основной путь подсвечен зелёным, резервный — янтарным.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge>
            <Route className="h-3.5 w-3.5" /> Узлов: {nodes.length}
          </Badge>
          <Badge>Рёбер: {projectedEdges.length}</Badge>
          {skippedNodeCount > 0 ? <Badge>Без координат узлов: {skippedNodeCount}</Badge> : null}
          {skippedEdgeCount > 0 ? <Badge>Пропущено рёбер: {skippedEdgeCount}</Badge> : null}
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-4">
        <div className="rounded-3xl border border-slate-200 p-4 text-sm dark:border-slate-800">
          <div className="font-medium">Инспектор</div>
          {selection ? (
            <>
              <div className="mt-3 text-sm font-semibold text-slate-900 dark:text-slate-100">{selection.title}</div>
              <pre className="mt-3 whitespace-pre-wrap text-xs text-slate-500 dark:text-slate-400">{selection.body}</pre>
            </>
          ) : (
            <div className="mt-3 text-slate-500 dark:text-slate-400">Кликни по узлу или ребру, чтобы увидеть детали.</div>
          )}
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-2 dark:border-slate-800 dark:bg-slate-950">
          {nodes.length === 0 ? (
            <div className="flex h-[640px] items-center justify-center text-sm text-slate-500 dark:text-slate-400">
              Для текущих локаций нет валидных geo-координат. Карта отображается после появления lat/lon.
            </div>
          ) : (
            <svg viewBox={`0 0 ${width} ${height}`} className="h-[640px] w-full rounded-2xl bg-slate-50 dark:bg-slate-900" role="img" aria-label="Гео-карта сети">
              <rect x={0} y={0} width={width} height={height} fill="transparent" />

              {projectedEdges.map(({ edge, from, to, isPrimary, isBackup }) => {
                const stroke = isPrimary ? '#16a34a' : isBackup ? '#f59e0b' : '#cbd5e1';
                const opacity = isPrimary ? 1 : isBackup ? 0.95 : 0.55;
                const strokeWidth = isPrimary ? 3.2 : isBackup ? 2.6 : 1.6;

                return (
                  <line
                    key={`${edge.id}:${from.id}:${to.id}`}
                    x1={from.x}
                    y1={from.y}
                    x2={to.x}
                    y2={to.y}
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    opacity={opacity}
                    onClick={() => {
                      setSelection({
                        title: edge.subject || edge.id,
                        body: `${edge.from} → ${edge.to}\nLatency: ${formatLatency(effectiveLatency(edge))}\nCapacity: ${formatCapacity(edge.capacityMbps)}\nVendor: ${edge.vendorName || '—'}`,
                      });
                    }}
                    className="cursor-pointer"
                  />
                );
              })}

              {nodes.map((node) => (
                <g
                  key={node.id}
                  onClick={() => {
                    setSelection({
                      title: node.id,
                      body: `lat: ${node.lat.toFixed(6)}\nlon: ${node.lon.toFixed(6)}\nУзел ${node.inRoute ? 'в маршруте' : 'в сети'}`,
                    });
                  }}
                  className="cursor-pointer"
                >
                  <circle
                    cx={node.x}
                    cy={node.y}
                    r={node.inRoute ? 6 : 4.2}
                    fill={node.inRoute ? '#0ea5e9' : '#94a3b8'}
                    stroke={node.inRoute ? '#0369a1' : '#64748b'}
                    strokeWidth={1}
                  />
                </g>
              ))}
            </svg>
          )}
        </div>
      </div>
    </Card>
  );
}
